import * as THREE from 'three';
import { NoiseGenerator } from '@/terrain/NoiseGenerator';
import { TerrainMaterial } from './TerrainMaterial';
import type { TerrainConfig } from '@/types/terrain';

export class TerrainGenerator {
    private size: number;
    private segments: number;
    private amplitude: number;
    private scale: number;
    private seed: number | string;
    private useVertexColors: boolean;

    private noiseGenerator: NoiseGenerator;
    private mesh: THREE.Mesh | null;

    private minHeight: number;
    private maxHeight: number;

    private octaves: number;
    private persistence: number;
    private lacunarity: number;
    private useFBM: boolean;

    constructor(config: TerrainConfig = {}) {
        // 기본 설정
        this.size = config.size ?? 50;
        this.segments = config.segments ?? 100;
        this.amplitude = config.amplitude ?? 5;
        this.scale = config.scale ?? 0.1;
        this.seed = config.seed ?? 'terra-noise';
        this.useVertexColors = config.useVertexColors ?? true;

        // 노이즈 생성기
        this.noiseGenerator = new NoiseGenerator({
            seed: typeof this.seed === 'number' ? this.seed : undefined
        });

        // 지형 메시
        this.mesh = null;

        // 높이 범위 추적 (정규화용)
        this.minHeight = Infinity;
        this.maxHeight = -Infinity;

        // FBM 설정
        this.octaves = config.octaves ?? 4;
        this.persistence = config.persistence ?? 0.5;
        this.lacunarity = config.lacunarity ?? 2.0;
        this.useFBM = config.useFBM ?? true;
    }

    /**
     * 지형 생성
     * @returns {THREE.Mesh} 생성된 지형 메시
     */
    generate(): THREE.Mesh {
        // 1. 지오메트리 생성
        const geometry = new THREE.PlaneGeometry(
            this.size,
            this.size,
            this.segments,
            this.segments
        );

        const positions = geometry.attributes.position.array as Float32Array;
        const vertexCount = positions.length / 3;

        const heights: number[] = [];
        for (let i = 0; i < positions.length; i += 3) {
            const x = positions[i];
            const y = positions[i + 1];

            let noiseValue: number;

            if (this.useFBM) {
                // FBM 사용
                noiseValue = this.noiseGenerator.getFBM(
                    x, y,
                    this.scale,
                    this.octaves,
                    this.persistence,
                    this.lacunarity
                );
            } else {
                // 단순 노이즈
                noiseValue = this.noiseGenerator.getNoise(x, y, this.scale);
            }

            const height = noiseValue * this.amplitude;
            positions[i + 2] = height;
            heights.push(height);

            this.minHeight = Math.min(this.minHeight, height);
            this.maxHeight = Math.max(this.maxHeight, height);
        }

        // create vertex colors
        if (this.useVertexColors) {
            const colors = new Float32Array(vertexCount * 3);

            for (let i = 0; i < vertexCount; i++) {
                const height = heights[i];

                const normalizedHeight = (height - this.minHeight) / (this.maxHeight - this.minHeight);

                const color = TerrainMaterial.getColorByHeight(normalizedHeight);

                colors[i * 3] = color.r;
                colors[i * 3 + 1] = color.g;
                colors[i * 3 + 2] = color.b;
            }
            // add colors attributes
            geometry.setAttribute(
                'color',
                new THREE.BufferAttribute(colors, 3)
            );
        }

        geometry.computeVertexNormals();
        geometry.attributes.position.needsUpdate = true;

        const material = TerrainMaterial.createMaterial(this.useVertexColors);

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.rotation.x = -Math.PI / 2;

        return this.mesh;
    }

    /**
     * 기존 메시의 지형 업데이트
     */
    update(): void {
        if (!this.mesh) return;

        const geometry = this.mesh.geometry as THREE.BufferGeometry;
        const positions = geometry.attributes.position.array as Float32Array;
        const colors = geometry.attributes.color?.array as Float32Array | undefined;
        const vertexCount = positions.length / 3;

        // reset height range
        this.minHeight = Infinity;
        this.maxHeight = -Infinity;

        // 버텍스 높이 재계산
        const heights: number[] = [];
        for (let i = 0; i < positions.length; i += 3) {
            const x = positions[i];
            const y = positions[i + 1];

            const noiseValue = this.noiseGenerator.getNoise(x, y, this.scale);
            const height = noiseValue * this.amplitude;

            // set 'z' value as height
            positions[i + 2] = height;
            heights.push(height);

            this.minHeight = Math.min(this.minHeight, height);
            this.maxHeight = Math.max(this.maxHeight, height);
        }

        if (this.useVertexColors && colors) {
            for (let i = 0; i < vertexCount; i++) {
                const height = heights[i];
                const normalizedHeight = (height - this.minHeight) /
                    (this.maxHeight - this.minHeight);

                const color = TerrainMaterial.getColorByHeight(normalizedHeight);

                colors[i * 3] = color.r;
                colors[i * 3 + 1] = color.g;
                colors[i * 3 + 2] = color.b;
            }
            geometry.attributes.color.needsUpdate = true;
        }

        // 노멀 재계산 및 업데이트
        geometry.computeVertexNormals();
        geometry.attributes.position.needsUpdate = true;
    }

    /**
     * 설정 변경 메서드들
     */
    setAmplitude(value: number): void {
        this.amplitude = value;
        this.update();
    }

    setScale(value: number): void {
        this.scale = value;
        this.update();
    }

    setSeed(value: number | string): void {
        this.seed = value;
        this.noiseGenerator = new NoiseGenerator({
            seed: typeof value === 'number' ? value : undefined
        });
        this.update();
    }

    setOctaves(value: number): void {
        this.octaves = value;
        this.update();
    }

    setPersistence(value: number): void {
        this.persistence = value;
        this.update();
    }

    setLacunarity(value: number): void {
        this.lacunarity = value;
        this.update();
    }

    setUseFBM(value: boolean): void {
        this.useFBM = value;
        this.update();
    }

    /**
     * 메시 반환
     */
    getMesh(): THREE.Mesh | null {
        return this.mesh;
    }
}
